/**
 * Role-based access control.
 *
 * A user holds one role per property (see the Membership model). Routes declare
 * the permission they need; the role matrix below is the only place that decides
 * who holds it. OWNER is the only role that can manage users and properties.
 */
export const ROLES = [
    'OWNER',
    'MANAGER',
    'ACCOUNTANT',
    'FRONT_DESK',
    'CASHIER',
    'STOREKEEPER',
    'KITCHEN',
    'HOUSEKEEPING',
];
export const PERMISSIONS = [
    'dashboard:view',
    'pos:operate',
    'pos:bill',
    'pos:discount',
    'pos:void',
    'kds:view',
    'kds:update',
    'menu:view',
    'menu:manage',
    'menu:price',
    'inventory:view',
    'inventory:adjust',
    'inventory:manage',
    'procurement:view',
    'procurement:create',
    'procurement:approve',
    'procurement:receive',
    'rooms:view',
    'rooms:checkin',
    'rooms:checkout',
    'rooms:manage',
    'housekeeping:view',
    'housekeeping:update',
    'reservation:view',
    'reservation:manage',
    'invoice:view',
    'invoice:void',
    /** Email an invoice to the customer's address on record. */
    'invoice:email',
    /** Email an invoice to any address typed in. Owner only - see ROLE_PERMISSIONS. */
    'invoice:email-custom',
    'hr:view',
    'hr:manage',
    'payroll:view',
    'payroll:run',
    'reports:view',
    'gst:export',
    'settings:view',
    'settings:manage',
    'users:view',
    'users:manage',
    'audit:view',
    'property:manage',
    'expense:view',
    'expense:create',
    'expense:void',
    'expense:category:manage',
    'daybook:view',
    'pnl:view',
    /*
     * Privacy and data governance: consent records, notices, data principal
     * requests, retention, the hotel's view of support access. View is reading
     * those registers; manage is acting on them (recording consent, working a
     * request, publishing a guest notice).
     */
    'privacy:view',
    'privacy:manage',
    /*
     * Guests' personal data, apart from the room rack's operational view.
     * guest:view is who is staying (name, folio, online bookings);
     * guest:contact:view adds phone, email and GSTIN. Housekeeping, kitchen
     * and stores hold neither: a room to clean does not need a name or a
     * phone number. No permission shows an identity-document number - no
     * screen reads one back; its last four appear only in a guest's own data
     * export, under privacy:manage.
     */
    'guest:view',
    'guest:contact:view',
    /** SARTHI's invoices and payments for this account's subscription. */
    'billing:view',
];
const FRONT_OF_HOUSE = [
    'dashboard:view',
    'pos:operate',
    'pos:bill',
    'kds:view',
    'menu:view',
    'invoice:view',
    'invoice:email',
    'reservation:view',
    'reservation:manage',
    /*
     * Whoever bills a ticket also needs to spend petty cash from the same
     * till (vegetables, delivery charges) - both CASHIER and FRONT_DESK share
     * this base. Neither gets expense:void: a mistaken entry is a manager's
     * call, same as pos:void/invoice:void.
     */
    'expense:view',
    'expense:create',
];
export const ROLE_PERMISSIONS = {
    OWNER: [...PERMISSIONS],
    /*
     * Everything on the floor, and nothing under Administration.
     *
     * Payments, users, the property profile and the audit trail belong to the
     * owner. `settings:view` stays, because the menu screen reads the target
     * food cost through it and the QR sheet reads the menu link - taking it
     * away would break two screens a manager runs every day.
     */
    MANAGER: PERMISSIONS.filter((p) => ![
        'users:view',
        'users:manage',
        'settings:manage',
        'audit:view',
        'property:manage',
        // Sending a guest's invoice to an address nobody has verified is a
        // disclosure of their details, so it stays with the owner.
        'invoice:email-custom',
        // Payments belong to the owner (above); the subscription bill with them.
        'billing:view',
    ].includes(p)),
    ACCOUNTANT: [
        'dashboard:view',
        'invoice:view',
        'invoice:email',
        'reports:view',
        'gst:export',
        'payroll:view',
        'payroll:run',
        'hr:view',
        'procurement:view',
        'inventory:view',
        'menu:view',
        'settings:view',
        'expense:view',
        'daybook:view',
        'pnl:view',
        // The subscription's tax invoices are the hotel's purchase records.
        'billing:view',
    ],
    FRONT_DESK: [
        ...FRONT_OF_HOUSE,
        'rooms:view',
        'rooms:checkin',
        'rooms:checkout',
        'housekeeping:view',
        'guest:view',
        'guest:contact:view',
    ],
    /*
     * Cashiers reconcile the till at the end of a shift, so they get the Day
     * Book (sales + expenses for the day) alongside the rest of the till -
     * see FRONT_OF_HOUSE above for the same reasoning on expense:view/create.
     * Not extended to FRONT_DESK: front desk wasn't asked for this, and unlike
     * expense:view/create it isn't implied by billing a ticket.
     */
    // guest:view: posting a ticket to a room means finding the guest by name.
    CASHIER: [...FRONT_OF_HOUSE, 'pos:discount', 'rooms:view', 'daybook:view', 'guest:view'],
    STOREKEEPER: [
        'dashboard:view',
        'inventory:view',
        'inventory:adjust',
        'inventory:manage',
        'procurement:view',
        'procurement:create',
        'procurement:receive',
        'menu:view',
        'reports:view',
    ],
    KITCHEN: ['kds:view', 'kds:update', 'menu:view', 'inventory:view'],
    HOUSEKEEPING: ['housekeeping:view', 'housekeeping:update', 'rooms:view'],
};
export const roleHas = (role, permission) => ROLE_PERMISSIONS[role].includes(permission);
export const permissionsFor = (role) => [...ROLE_PERMISSIONS[role]];
export const ROLE_LABELS = {
    OWNER: 'Owner',
    MANAGER: 'General manager',
    ACCOUNTANT: 'Accountant',
    FRONT_DESK: 'Front desk',
    CASHIER: 'Cashier',
    STOREKEEPER: 'Storekeeper',
    KITCHEN: 'Kitchen',
    HOUSEKEEPING: 'Housekeeping',
};
export const ROLE_DESCRIPTIONS = {
    OWNER: 'Full access, including users, properties and tenant settings.',
    MANAGER: 'Runs the property day to day; everything except user and property administration.',
    ACCOUNTANT: 'Read-only on operations, with payroll runs and GST filing exports.',
    FRONT_DESK: 'Room rack, check-in and check-out, reservations, restaurant tickets.',
    CASHIER: 'Opens and settles restaurant tickets, applies discounts.',
    STOREKEEPER: 'Stock counts, wastage, purchase orders and goods receipts.',
    KITCHEN: 'Kitchen display: sees and advances tickets for its stations.',
    HOUSEKEEPING: 'Room cleaning tasks and housekeeping status only.',
};
//# sourceMappingURL=permissions.js.map